import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useMarketingConsent } from "@core/hooks/useMarketingConsent";

/**
 * Le seul interrupteur de désinscription du produit (décision du 15/09/2026).
 *
 * Il vit sur `/data-control`, qui est l'URL donnée dans le pied de page des
 * e-mails optionnels et de la relance J+7. Si cette URL change, changer aussi
 * `_shared/email-preferences-link.ts` et `_shared/trial-email-copy.ts` — le
 * contrôle `check-email-i18n` vérifie la route nommément, il rougira.
 *
 * La carte affiche la DATE du consentement quand il existe. Ce n'est pas un
 * détail décoratif : c'est ce qui permet de répondre à « quand ai-je accepté ? »
 * sans aller lire la base, et c'est la raison pour laquelle la colonne est un
 * horodatage et non un booléen.
 */
export const MarketingConsentCard: React.FC = () => {
  // Namespace « polls » : c'est là que vit le bloc dataControl, comme pour
  // DataControlUI qui héberge cette carte.
  const { t, i18n } = useTranslation("polls");
  const { user } = useAuth();
  const { optedInAt, isOptedIn, loading, saving, error, setConsent } =
    useMarketingConsent();

  if (!user || loading) return null;

  const dateLisible = optedInAt
    ? optedInAt.toLocaleDateString(i18n.language || "fr", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div
      className="flex items-start justify-between gap-4 rounded-lg bg-muted/40 p-4"
      data-testid="marketing-consent-card"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-muted-foreground">
          {t("dataControl.marketing.description")}
        </p>

        {isOptedIn && dateLisible && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("dataControl.marketing.given_on", { date: dateLisible })}
          </p>
        )}

        {/* Dire ce qui NE s'arrête PAS est aussi important que l'interrupteur :
            sans cette phrase, quelqu'un qui coupe peut croire qu'il ne recevra
            plus l'avertissement avant suppression de ses sondages. */}
        <p className="mt-2 text-xs text-muted-foreground">
          {t("dataControl.marketing.still_sent")}
        </p>

        {error && (
          <p
            role="alert"
            className="mt-2 flex items-center gap-1.5 text-xs text-destructive"
          >
            <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
            {t("dataControl.marketing.error")}
          </p>
        )}
      </div>

      <label className="relative inline-flex flex-shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          checked={isOptedIn}
          disabled={saving}
          onChange={(e) => void setConsent(e.target.checked)}
          className="peer sr-only"
          aria-label={t("dataControl.marketing.title")}
          data-testid="marketing-consent-toggle"
        />
        <div className="peer h-7 w-14 rounded-full bg-input after:absolute after:left-[2px] after:top-[2px] after:h-6 after:w-6 after:rounded-full after:border after:border-input after:bg-background after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-7 peer-checked:after:border-background peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50" />
        {saving && (
          <Loader2
            className="ml-2 h-4 w-4 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </label>
    </div>
  );
};
